/**
 * 操作ログ一覧ページ
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Title, Stack, Group, Box, ActionIcon, Flex } from "@mantine/core";
import "dayjs/locale/ja";
import { History, RotateCw } from "lucide-react";
import { formatDateTime } from "../utils/formatters";
import {
  fetchOperationLogs,
  fetchOperationLogDetail,
  OperationLog,
} from "../services/operationLogService";
import {
  OperationLogFilterSidebar,
  OperationLogFilters,
} from "../components/operation-logs/OperationLogFilterSidebar";
import { OperationLogList } from "../components/operation-logs/OperationLogList";

/**
 * 操作ログ一覧ページ
 */
export function OperationLogsPage() {
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  // フィルターステート
  const [filters, setFilters] = useState<OperationLogFilters>({
    actionFilter: [],
    resourceFilter: [],
    startDate: null,
    endDate: null,
  });

  // 詳細データのキャッシュ（遅延読み込み用）
  const [openedIds, setOpenedIds] = useState<string[]>([]);
  const [logDetails, setLogDetails] = useState<Record<string, OperationLog>>({});
  const [loadingDetails, setLoadingDetails] = useState<Record<string, boolean>>({});

  // スクロール制御用
  const viewport = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    viewport.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadLogs = useCallback(
    async (isBackground = false) => {
      if (!isBackground) setLoading(true);
      try {
        const data = await fetchOperationLogs({
          page,
          pageSize,
          action: filters.actionFilter || undefined,
          resourceType: filters.resourceFilter || undefined,
          startDate: filters.startDate,
          endDate: filters.endDate,
        });
        setLogs(data.logs);
        setTotal(data.total);
      } catch (error) {
        console.error("Failed to fetch operation logs:", error);
      } finally {
        if (!isBackground) setLoading(false);
      }
    },
    [page, filters]
  );

  useEffect(() => {
    loadLogs();
    scrollToTop();
  }, [loadLogs]);

  const fetchDetail = async (id: string) => {
    if (logDetails[id] || loadingDetails[id]) return;

    setLoadingDetails((prev) => ({ ...prev, [id]: true }));
    try {
      const detail = await fetchOperationLogDetail(id);
      setLogDetails((prev) => ({ ...prev, [id]: detail }));
    } catch (error) {
      console.error(`Failed to fetch detail for log ${id}:`, error);
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleFiltersChange = (newFilters: OperationLogFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      actionFilter: [],
      resourceFilter: [],
      startDate: null,
      endDate: null,
    });
    setPage(1);
  };

  const handleOpenedIdsChange = (ids: string[]) => {
    setOpenedIds(ids);
    ids.forEach((id) => fetchDetail(id));
  };

  return (
    <Box
      style={{
        height: "calc(100vh - var(--app-shell-header-offset) - var(--app-shell-padding) * 2)",
        overflow: "hidden",
      }}
    >
      {/* AIアシスタント向けガイド */}
      <div data-ai-guide hidden>
        {`[ページ: 操作ログ]
[概要: 管理者の操作履歴を監査・確認できる画面]
[構成: 左=フィルターエリア, 右=ログ一覧（アコーディオン形式）]
[表示件数: ${logs.length}件 / 全${total}件 (ページ${page}/${Math.ceil(total / pageSize) || 1})]
[フィルター条件]
- アクション: ${filters.actionFilter.length > 0 ? filters.actionFilter.join(", ") : "全て"}
- 対象: ${filters.resourceFilter.length > 0 ? filters.resourceFilter.join(", ") : "全て"}
- 期間: ${filters.startDate ? formatDateTime(filters.startDate) : "指定なし"} ～ ${filters.endDate ? formatDateTime(filters.endDate) : "指定なし"}
[アクションの意味]
- LOGIN: ログイン操作
- CREATE_*: 新規作成 (顧客、テンプレート、管理者など)
- UPDATE_*: 更新操作
- DELETE_*: 削除操作
- REPROCESS_LOG: エラーログの再処理
[リソースの意味]
- auth: 認証関連 (ログインなど)
- customer: 顧客情報
- csv_template: CSVテンプレート
- admin: 管理者アカウント
- log: 処理ログの操作
[操作のヒント]
- 行をクリックするとJSON形式で詳細データを確認可能
- 「成功/失敗」でAPIコールの結果がわかる
- 監査や問題調査に活用できる`}
      </div>

      <Flex gap="md" h="100%" align="stretch" style={{ overflow: "hidden" }}>
        {/* 左サイドバー: フィルターエリア */}
        <Box
          style={{
            width: 420,
            minWidth: 340,
            height: "100%",
            flexShrink: 0.3,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack gap="sm" h="100%">
            <Group gap="xs" px="xs">
              <History size={24} />
              <Title order={3}>操作ログ</Title>
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => loadLogs()}
                loading={loading}
              >
                <RotateCw size={18} />
              </ActionIcon>
            </Group>

            <OperationLogFilterSidebar
              filters={filters}
              onChange={handleFiltersChange}
              onReset={handleResetFilters}
            />
          </Stack>
        </Box>

        {/* 右メイン: ログ一覧エリア */}
        <Box
          style={{
            flex: 1,
            height: "100%",
            minWidth: 400,
            flexShrink: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <OperationLogList
            logs={logs}
            loading={loading}
            total={total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            openedIds={openedIds}
            onOpenedIdsChange={handleOpenedIdsChange}
            logDetails={logDetails}
            loadingDetails={loadingDetails}
            viewportRef={viewport}
          />
        </Box>
      </Flex>
    </Box>
  );
}
